<template>
  <v-row class="mt-10">
    <!-- IZQUIERDA: TREEVIEW -->
    <v-col cols="12" md="6">
      <v-card class="pa-4" outlined>
        <v-card-title class="headline mb-2"> Variables disponibles </v-card-title>
        <v-card-subtitle class="mb-4">
          Selecciona variables para agregarlas a tu plantilla.
          <div class="d-flex">
            <v-btn
              small
              color="deep-purple mr-2"
              class="white--text mb-4"
              @click="verVariables(plantilla)">
              Cargar Data
            </v-btn>
            <v-btn small color="deep-purple" class="white--text mr-2" @click="expandAll">
              Expandir todo
            </v-btn>

            <v-btn small color="grey darken-1" class="white--text" @click="collapseAll">
              Contraer todo
            </v-btn>
          </div>
        </v-card-subtitle>

        <!-- Contenedor scroll -->
        <div
          ref="variablesTree"
          style="
            height: 800px;
            overflow-y: auto;
            border: 1px solid #eee;
            border-radius: 8px;
            position: relative;
          ">
          <v-treeview
            ref="treeComponent"
            :items="localTree"
            hoverable
            activatable
            item-key="fullPath"
            return-object
            :transition="false">
            <template v-slot:prepend="{ item }">
              <v-checkbox
                dense
                color="deep-purple"
                :class="{ 'missing-checkbox': item.missing }"
                v-model="item.checked"
                @change="toggleSelection(item)"></v-checkbox>
            </template>

            <!-- Vista del nodo -->
            <template v-slot:label="{ item }">
              <div
                :id="'node-' + item.fullPath"
                class="tree-label"
                :class="{
                  'highlight-node': highlightedNode === item.fullPath,
                  'missing-node': item.missing,
                }"
                @click="verEnLista(item.fullPath)">
                <div class="tree-label-name">
                  <strong>{{ item.name }}</strong>
                </div>

                <div v-if="item.value !== null && item.value !== ''" class="tree-label-value">
                  <v-chip
                    small
                    color="deep-purple lighten-5"
                    text-color="deep-purple darken-2"
                    class="mt-1">
                    {{ formatearValorVariable(item.value) }}
                  </v-chip>
                </div>
                <div v-else-if="item.missing" class="tree-label-value">
                  <v-chip small class="mt-1 missing-chip">
                    No presente en este registro
                  </v-chip>
                </div>
              </div>
            </template>
          </v-treeview>
        </div>
      </v-card>
    </v-col>

    <!-- DERECHA: VARIABLES SELECCIONADAS -->
    <v-col cols="12" md="6">
      <v-card class="pa-4" outlined>
        <v-card-title class="headline mb-2"> Variables seleccionadas </v-card-title>

        <v-card-subtitle class="mb-4">
          Aquí verás todas las variables que has marcado.
          <div class="d-flex">
            <v-btn small outlined color="deep-purple" class="mt-2" @click="crearAliasDeVariables()">
              Crear Alias a todas las variables
            </v-btn>
            <v-btn small outlined color="grey" class="mt-2 ml-2" @click="limpiarAliasDeVariables()">
              Limpiar todos los Alias
            </v-btn>
          </div>
        </v-card-subtitle>
        <!-- Contenedor scroll -->
        <div
          ref="selectedVariablesList"
          style="
            height: 800px;
            overflow-y: auto;
            border: 1px solid #eee;
            border-radius: 8px;
            position: relative;
          ">
          <v-alert
            v-if="selectedVariables.length === 0"
            type="info"
            border="left"
            colored-border
            elevation="1">
            {{ $t('bannerMessages.noVariablesYet') }}
          </v-alert>

          <v-list two-line>
            <v-list-item
              v-for="item in selectedVariables"
              :key="item.fullPath"
              :id="'selected-' + item.fullPath"
              class="mb-3"
              :class="{
                'missing-variable': item.missing,
                'highlight-selected': highlightedSelected === item.fullPath,
              }">
              <v-list-item-content>
                <!-- TEXTFIELD inteligente -->
                <v-text-field
                  dense
                  outlined
                  hide-details
                  v-model="item.alias"
                  :label="item.fullPath"
                  :class="{ 'missing-variable': item.missing }"
                  @blur="$emit('update-alias', { fullPath: item.fullPath, alias: item.alias })" />

                <!-- Valor (chip) -->
                <v-chip
                  small
                  class="mt-1"
                  :class="{ 'missing-chip': item.missing }"
                  :color="item.missing ? undefined : 'deep-purple lighten-5'"
                  :text-color="item.missing ? undefined : 'deep-purple darken-2'"
                  @click="verEnArbol(item.fullPath)"
                  style="cursor: pointer">
                  {{
                    item.missing
                      ? 'No presente en este registro'
                      : `valor: ${formatearValorVariable(item.value)}`
                  }}
                </v-chip>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </div>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import moment from 'moment'
import { esBase64Imagen } from '@/util/libreriaFormatos/imagenes/base64.js'

export default {
  name: 'formatos-create',
  props: {
    tree: Array,
    highlighted: String,
    plantilla: Object,
  },
  data() {
    return {
      highlightedNode: null,
      highlightedSelected: null,
      localTree: [],
      ui: {
        loading: false,
      },
    }
  },

  computed: {
    selectedVariables() {
      const list = []
      const traverse = (items) => {
        items.forEach((i) => {
          if (i.checked) list.push(i)
          if (i.children?.length) traverse(i.children)
        })
      }
      traverse(this.localTree)
      return list
    },
  },
  watch: {
    plantilla: {
      deep: true,
      immediate: true,
      handler(val) {
        if (!val || !val.refUuid) return

        // Evita múltiples llamadas si ya se cargó
        if (this.localTree.length > 0) return

        this.verVariables(val)
      },
    },

    tree: {
      immediate: true,
      deep: true,
      handler(val) {
        this.localTree = JSON.parse(JSON.stringify(val))
      },
    },

    highlighted(val) {
      this.highlightedNode = val
    },
  },

  methods: {
    esValorBase64(valor) {
      return esBase64Imagen(valor)
    },
    formatearValorVariable(valor) {
      if (this.esValorBase64(valor)) return 'Imagen detectada'
      return valor
    },
    verVariables(plantilla) {
      this.$store
        .dispatch('formato-plantillas/downloadVariablesDisponibles', plantilla.refUuid)
        .then((response) => {
          const datos = response.formatoAnidado
          const tree = this.buildTree(datos || {})

          const guardadas = Array.isArray(this.plantilla?.keynames?.variables)
            ? this.plantilla.keynames.variables
            : []
          guardadas.forEach((v) => {
            if (v?.fullPath) this.ensurePath(tree, v.fullPath)
          })

          this.localTree = JSON.parse(JSON.stringify(tree))

          this.$nextTick(() => this.expandAll())

          // Aplicar alias guardados
          if (this.plantilla.keynames) {
            const map = (items) => {
              items.forEach((node) => {
                const saved = guardadas.find((k) => k.fullPath === node.fullPath)
                if (saved) {
                  node.checked = true
                  node.alias = saved.alias || ''
                  node.expresion = saved.expresion || ''
                }
                if (node.children?.length) map(node.children)
              })
            }
            map(this.localTree)
            this.$emit('update-tree', this.localTree)
          }
        })
    },

    buildTree(data, parentKey = '') {
      return Object.entries(data).map(([key, value]) => {
        const fullPath = parentKey ? `${parentKey}.${key}` : key

        const node = {
          id: fullPath,
          name: key,
          fullPath,
          checked: false,
          alias: '',
          expresion: '', // ← AÑADIR
          children: [],
          value: null,
          missing: false,
        }

        // Valor primitivo
        if (
          value === null ||
          typeof value === 'string' ||
          typeof value === 'number' ||
          typeof value === 'boolean'
        ) {
          node.value = value

          // 🔥 ASIGNAR EXPRESIÓN AUTOMÁTICA
          node.expresion = this.esValorBase64(value) ? `\${i:${fullPath}}` : `\${v:${fullPath}}`

          return node
        }

        // Array
        if (Array.isArray(value)) {
          node.children = value.map((item, index) => {
            const childPath = `${fullPath}.${index}`

            if (
              item === null ||
              typeof item === 'string' ||
              typeof item === 'number' ||
              typeof item === 'boolean'
            ) {
              return {
                id: childPath,
                name: `[${index}]`,
                fullPath: childPath,
                checked: false,
                alias: '',
                value: item,
                expresion: this.esValorBase64(item) ? `\${i:${childPath}}` : `\${v:${childPath}}`,
                children: [],
                missing: false,
              }
            }

            return {
              id: childPath,
              name: `[${index}]`,
              fullPath: childPath,
              checked: false,
              alias: '',
              value: null,
              expresion: '',
              children: this.buildTree(item, childPath),
              missing: false,
            }
          })

          return node
        }

        // Objeto → recursivo
        node.children = this.buildTree(value, fullPath)
        return node
      })
    },

    parsePath(fullPath) {
      const normalized = String(fullPath).replace(/\[(\d+)\]/g, '.$1').replace(/^\./, '')
      return normalized
        .split('.')
        .filter(Boolean)
        .map((segment) => ({
          key: segment,
          isIndex: /^\d+$/.test(segment),
        }))
    },

    ensurePath(tree, fullPath) {
      const segmentos = this.parsePath(fullPath)
      let items = tree
      let currentPath = ''

      segmentos.forEach((seg, idx) => {
        currentPath = currentPath ? `${currentPath}.${seg.key}` : seg.key
        let node = items.find((n) => n.fullPath === currentPath)

        if (!node) {
          node = {
            id: currentPath,
            name: seg.isIndex ? `[${seg.key}]` : seg.key,
            fullPath: currentPath,
            checked: false,
            alias: '',
            expresion: '',
            children: [],
            value: null,
            missing: true,
          }
          items.push(node)
        }

        if (idx < segmentos.length - 1) {
          if (!node.children) node.children = []
          items = node.children
        }
      })
    },

    toggleSelection(item) {
      // 1. Asignar expresión automática
      if (item.checked) {
        item.expresion = this.esValorBase64(item.value)
          ? `\${i:${item.fullPath}}`
          : `\${v:${item.fullPath}}`
      } else {
        item.expresion = ''
      }

      // 2. Emitir expresión individual al padre
      this.$emit('update-expresion-variable', {
        variableFullPath: item.fullPath,
        expresion: item.expresion,
      })

      // 3. Emitir alias si lo tiene
      this.$emit('update-alias', { fullPath: item.fullPath, alias: item.alias || '' })

      // 4. Emitir árbol completo ACTUALIZADO al padre
      this.$emit('update-tree', JSON.parse(JSON.stringify(this.localTree)))

      // 5. Enviar lista de seleccionados
      this.$emit('selection-changed', this.selectedVariables)
    },

    crearAliasDeVariables() {
      const contador = {}
      this.selectedVariables.forEach((variable) => {
        if (variable.alias?.trim()) return

        const partes = variable.fullPath.split('.')
        let base = partes[partes.length - 1] || 'valor'
        base = base.replace(/\[\d+]\s*$/, '')

        if (!contador[base]) contador[base] = 1
        else contador[base]++

        const numero = contador[base]
        const alias = `${base}N${numero}`

        // 🔥 ESTA LÍNEA QUE FALTABA
        variable.alias = alias

        this.$emit('update-alias', { fullPath: variable.fullPath, alias })
        this.$emit('update-expresion-variable', {
          variableFullPath: variable.fullPath,
          expresion: variable.expresion || '',
        })
      })

      this.$emit('update-tree', this.localTree)
      this.$emit('selection-changed', this.selectedVariables)
    },

    limpiarAliasDeVariables() {
      this.selectedVariables.forEach((v) => {
        this.$set(v, 'alias', '')
      })
    },

    verEnArbol(fullPath) {
      this.expandAll()
      this.$nextTick(() => {
        this.highlightedNode = fullPath
        const el = document.getElementById('node-' + fullPath)
        const container = this.$refs.variablesTree
        this.scrollToInContainer(container, el)

        setTimeout(() => {
          if (this.highlightedNode === fullPath) this.highlightedNode = null
        }, 3200)
      })
    },

    verEnLista(fullPath) {
      this.$nextTick(() => {
        const el = document.getElementById('selected-' + fullPath)
        if (!el) return
        this.highlightedSelected = fullPath
        const container = this.$refs.selectedVariablesList
        this.scrollToInContainer(container, el)

        setTimeout(() => {
          if (this.highlightedSelected === fullPath) this.highlightedSelected = null
        }, 3200)
      })
    },

    scrollToInContainer(container, el) {
      if (!container || !el) return
      const containerTop = container.getBoundingClientRect().top
      const elTop = el.getBoundingClientRect().top
      const offset = elTop - containerTop
      const target =
        container.scrollTop + offset - container.clientHeight / 2 + el.clientHeight / 2
      container.scrollTo({ top: target, behavior: 'smooth' })
    },

    expandAll() {
      this.$nextTick(() => {
        if (this.$refs.treeComponent?.updateAll) {
          this.$refs.treeComponent.updateAll(true)
        }
      })
    },

    collapseAll() {
      this.$nextTick(() => {
        if (this.$refs.treeComponent?.updateAll) {
          this.$refs.treeComponent.updateAll(false)
        }
      })
    },
  },

  created() {
    this.$store.commit('menu/setPageTitle', 'Configuración de plantilla')
  },
}
</script>

<style>
.tree-label {
  display: flex;
  flex-direction: column;
}

.tree-label-name {
  font-size: 14px;
}

.tree-label-value {
  font-size: 12px;
  opacity: 0.8;
}

.missing-node .tree-label-name {
  color: #d35400;
  font-weight: 700;
}

.missing-node .tree-label-value {
  opacity: 0.9;
}

.missing-variable .v-label,
.missing-variable .v-input__slot,
.missing-variable .v-input__control {
  color: #d35400 !important;
}

.missing-checkbox .v-icon {
  color: #d35400 !important;
}

.missing-chip {
  background-color: #ffe1c4 !important;
  color: #8a3b00 !important;
  border: 1px solid #f0a35a !important;
}

.highlight-selected {
  background: #c7efe4;
  border-radius: 6px;
  transition: background 0.6s ease;
}

.highlight-node {
  background: #6adbc3 !important;
  transition: background 0.6s ease;
  border-radius: 4px;
  padding: 4px;
}
</style>
